import type { ProductSizeOption } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { type DestinationFieldErrors, DestinationSizeRows } from '@/entities/quote';
import { Button, FormField, PostcodeSearchFields } from '@/shared/ui';
import type { GuestDestinationValues } from '../model/use-submit-guest-quote-request';

export interface GuestDestinationCardProps {
  /** 1부터 — 카드 제목 "배송지 N" 과 삭제 버튼 이름에 쓴다. */
  readonly order: number;
  readonly value: GuestDestinationValues;
  readonly errors: DestinationFieldErrors;
  readonly canRemove: boolean;
  readonly disabled?: boolean;
  readonly sizeOptions?: readonly ProductSizeOption[];
  readonly showQuantity?: boolean;
  readonly showSizeRows?: boolean;
  /** 바뀐 칸만 넘긴다 — 목록 훅이 이전 값과 병합한다. */
  readonly onChange: (patch: Partial<GuestDestinationValues>) => void;
  readonly onRemove: () => void;
}

/**
 * 비회원 견적 요청 배송지 카드(spec D8·§6-1) — 주소 검색(우편번호·기본 주소, 검색을 못 불러오면 직접 입력) + 수량 + [삭제](2장 이상).
 * 받는 분·연락처·상세 주소는 주문할 때 받는다(안내는 배송지 묶음 머리에 한 번).
 */
export const GuestDestinationCard = ({
  order,
  value,
  errors,
  canRemove,
  disabled = false,
  sizeOptions = [],
  showQuantity = sizeOptions.length === 0,
  showSizeRows = false,
  onChange,
  onRemove,
}: GuestDestinationCardProps): ReactElement => (
  // 제목(legend)을 띄워(float) 카드 안 첫 줄에 두고, [삭제]는 같은 줄 오른쪽 끝에 둔다(회원 카드와 같은 결).
  <fieldset disabled={disabled} className="relative min-w-0 border-t border-foreground/15 pt-5">
    <legend className="float-left text-base leading-9 font-semibold">배송지 {order}</legend>
    {canRemove ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="absolute top-6 right-0"
        aria-label={`배송지 ${order} 삭제`}
        onClick={onRemove}
      >
        삭제
      </Button>
    ) : null}

    <div className="clear-both grid min-w-0 gap-5 pt-4 sm:grid-cols-2">
      <PostcodeSearchFields
        value={{ postalCode: value.postalCode, address1: value.address1 }}
        errors={{ postalCode: errors.postalCode, address1: errors.address1 }}
        onChange={onChange}
      />

      {sizeOptions.length > 0 ? (
        showSizeRows ? (
          <DestinationSizeRows
            options={sizeOptions}
            rows={value.sizes}
            error={errors.sizes}
            onChange={(sizes) => onChange({ sizes })}
          />
        ) : null
      ) : showQuantity ? (
        <FormField
          label="수량"
          inputMode="numeric"
          autoComplete="off"
          isRequired
          required
          className="h-11 tabular-nums"
          value={value.quantity}
          error={errors.quantity}
          onChange={(event) => onChange({ quantity: event.target.value })}
        />
      ) : null}
    </div>
  </fieldset>
);
